import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppHeader from '../components/AppHeader';
import { useAuth } from '../lib/auth';
import { initialsFor } from '../lib/types';

export default function Profile() {
  const { user, profile, saveProfile } = useAuth();
  const navigate = useNavigate();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Seeded once, for the same reason the note editor is: the context's profile can change
  // underneath an open form, and resetting the fields mid-edit would be hostile.
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current || !profile) return;
    seeded.current = true;
    setFirstName(profile.firstName ?? '');
    setLastName(profile.lastName ?? '');
  }, [profile]);

  const dirty =
    firstName !== (profile?.firstName ?? '') || lastName !== (profile?.lastName ?? '');

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await saveProfile({ firstName: firstName.trim(), lastName: lastName.trim() });
      navigate('/');
    } catch (e) {
      const code = (e as { code?: string }).code ?? '';
      setError(`${(e as Error).message}${code ? ` (${code})` : ''}`);
      setSaving(false);
    }
  }

  return (
    <>
      <AppHeader title="Profile" />

      <main>
        <div className="identity">
          <span className="avatar lg">{initialsFor(profile, user?.displayName, user?.email)}</span>
          <span className="muted">{user?.email}</span>
        </div>

        <form
          className="edit"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <label className="field">
            <span className="field-label">First name</span>
            <input
              type="text"
              value={firstName}
              autoComplete="given-name"
              onChange={(e) => setFirstName(e.target.value)}
            />
          </label>

          <label className="field">
            <span className="field-label">Last name</span>
            <input
              type="text"
              value={lastName}
              autoComplete="family-name"
              onChange={(e) => setLastName(e.target.value)}
            />
          </label>

          <div className="field">
            <span className="field-label">Email</span>
            <p className="readonly">{user?.email ?? '—'}</p>
            {/* Changing this would mean changing the Firebase Auth identity — a different
                operation, with re-authentication and a verification mail. The phone apps
                edit a copy of it on the profile document; letting the web edit that copy
                would just make the two disagree. */}
            <p className="muted small hint">
              Your sign-in email. It is not editable here.
            </p>
          </div>

          {error && <p className="error">{error}</p>}

          <div className="actions">
            <button className="primary" type="submit" disabled={saving || !dirty}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button type="button" disabled={saving} onClick={() => navigate('/')}>
              Cancel
            </button>
          </div>
        </form>

        <p className="muted small">
          Reminder and voice-feedback settings live on this account too, but are set in the
          iOS and Android apps — they control behaviour the web client does not have.
        </p>
      </main>
    </>
  );
}
